'use client'

import {
  CheckCircle,
  Clock,
  GraduationCap,
  LoaderCircle,
  X,
  Zap,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useCallback, useRef, useState, useTransition } from 'react'
import { toast } from 'sonner'
import { checkAuthStatusAction } from '@/actions/auth/check-auth-status-action'
import { sendEventBrevo } from '@/actions/send-event-brevo'
import { getProfileAction } from '@/actions/user/get-profile-action'
import { trackEvent } from '@/components/@analitycs/trackEvent'
import ModalLayout from '@/components/enrolment-enter-modal/modal-layout'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { getActiveWorkloads } from '@/helpers/active-workloads'
import { useAuthStatus } from '@/hooks/use-auth-status'
import type { GetProductOutput } from '@/http/zamply/products/get-product-schema'
import { getBrevoProductProperties } from '@/utils/brevo-product-type'
import { createEnrolmentAction } from '../_actions/create-enrolment-action'
import { checkExistEnrolment } from '../_actions/exist-enrolment-action'

interface EnrolmentButtonProps {
  product: GetProductOutput
  variant?: 'primary' | 'secondary'
}

interface WorkloadButtonProps {
  workload: {
    reference: string
    workload_value: number
  }
  index: number
  isSelected: boolean
  isProcessing: boolean
  isPending: boolean
  onCreateEnrolment: (workload: {
    workload_value: number
    reference: string
  }) => void
}

const WorkloadButton = ({
  workload,
  index,
  isSelected,
  isProcessing,
  isPending,
  onCreateEnrolment,
}: WorkloadButtonProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      const nextButton = (
        e.currentTarget as HTMLElement
      ).parentElement?.nextElementSibling?.querySelector(
        'button'
      ) as HTMLButtonElement
      nextButton?.focus()
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault()
      const prevButton = (
        e.currentTarget as HTMLElement
      ).parentElement?.previousElementSibling?.querySelector(
        'button'
      ) as HTMLButtonElement
      prevButton?.focus()
    }
  }

  return (
    <div
      className={`group relative transition-all duration-300 ${
        isSelected ? 'z-10 scale-105' : 'hover:scale-[1.02]'
      }`}
    >
      <div className="relative">
        <button
          aria-checked={isSelected}
          aria-describedby={`workload-${workload.reference}`}
          className={`h-28 w-full overflow-hidden rounded-2xl border-2 p-6 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
            isSelected
              ? 'border-primary-500 bg-gradient-to-br from-primary-50 to-primary-50 shadow-lg shadow-primary-500/20'
              : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-md hover:shadow-primary-500/10'
          } ${isPending ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          disabled={isPending}
          onClick={() => onCreateEnrolment(workload)}
          onKeyDown={handleKeyDown}
          role="radio"
          tabIndex={index === 0 ? 0 : -1}
          type="button"
        >
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-2xl transition-all duration-300 ${
                isSelected
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-600'
              }`}
            >
              {isProcessing ? (
                <LoaderCircle className="h-6 w-6 animate-spin" />
              ) : (
                <Clock className="h-6 w-6" />
              )}
            </div>
            <span
              className={`font-bold text-base transition-colors duration-200 ${
                isSelected
                  ? 'text-primary-700'
                  : 'text-gray-700 group-hover:text-primary-600'
              }`}
            >
              {workload.workload_value} Horas
            </span>
          </div>

          {isSelected && (
            <div
              aria-hidden="true"
              className="-right-2 -top-2 zoom-in-95 absolute flex h-8 w-8 animate-in items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg duration-300"
            >
              <CheckCircle className="h-4 w-4 text-white" />
            </div>
          )}

          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 opacity-0 transition-opacity duration-300 group-hover:via-primary-500/5 group-hover:opacity-100" />
        </button>
      </div>

      <div className="sr-only" id={`workload-${workload.reference}`}>
        Opção de {workload.workload_value} horas de carga horária.
        {isSelected ? 'Selecionado.' : 'Não selecionado.'}
        {isProcessing ? 'Processando inscrição...' : ''}
      </div>
    </div>
  )
}

export default function EnrolmentButton({
  product,
  variant = 'primary',
}: EnrolmentButtonProps) {
  const isSubscriptionFree =
    product.billing_type === 'postpaid' || product.billing_type === 'free'
  const activeWorkloads = getActiveWorkloads(product.workloads)
  const [shouldShowFormAuth, setShouldShowFormAuth] = useState(false)
  const [shouldShowDialogWorkloads, setShouldShowDialogWorkloads] =
    useState(false)
  const [enrolmentData, setEnrolmentData] = useState<{
    product_id: string
    product_external_id: number
    workload_value: number
    workload_id: string
    period_id: string
  } | null>(null)
  const authStatus = useAuthStatus()

  const [isPending, startTransition] = useTransition()
  const [selectedWorkload, setSelectedWorkload] = useState<string | null>(null)
  const isCreatingEnrolmentRef = useRef(false)

  const router = useRouter()

  const handleCheckExistEnrolment = useCallback(() => {
    startTransition(async () => {
      const enrolmentExists = await checkExistEnrolment({
        reference: product.reference,
      })

      if (enrolmentExists.data) {
        if (enrolmentExists.data.success) {
          router.push(
            `/app/sala/curso/${product.external_id ?? product.reference}`
          )
        } else if (enrolmentExists.data.authenticated) {
          // Usuário autenticado mas sem inscrição - mostra dialog de workloads
          setShouldShowDialogWorkloads(true)
        } else {
          // Usuário não autenticado - mostra dialog de workloads para escolher primeiro
          setShouldShowDialogWorkloads(true)
        }
      }
    })
  }, [product.reference, product.external_id, router])

  const handleCreateEnrolment = useCallback(
    async (workload: { workload_value: number; reference: string }) => {
      // Aguarda a resolução do auth status para evitar pedir credenciais
      // a um usuário já logado durante a janela de carregamento inicial.
      if (authStatus === 'loading') {
        return
      }

      // Evita disparos duplicados de matrícula/tracking em caso de clique
      // duplo ou reentrada concorrente antes de isPending atualizar
      // (o startTransition só entra em vigor depois dos awaits abaixo).
      if (isCreatingEnrolmentRef.current) {
        return
      }
      isCreatingEnrolmentRef.current = true

      setSelectedWorkload(workload.reference)
      const payload = {
        product_id: product.reference,
        product_external_id: Number(product.external_id),
        workload_value: Number(workload.workload_value),
        workload_id: workload.reference,
        period_id: product.periods[0].reference,
      }

      setEnrolmentData(payload)
      // tirar isso
      router.refresh()

      let resolvedAuth: 'authenticated' | 'unauthenticated'
      try {
        resolvedAuth = await checkAuthStatusAction()
      } catch {
        resolvedAuth = 'unauthenticated'
      }

      if (resolvedAuth === 'unauthenticated') {
        isCreatingEnrolmentRef.current = false
        setShouldShowFormAuth(true)
        setShouldShowDialogWorkloads(false)
        return
      }

      // Usuário autenticado - prossegue com a inscrição
      startTransition(async () => {
        try {
          const [resultAction, { data: profileResult }] = await Promise.all([
            createEnrolmentAction({
              period_id: payload.period_id,
              product_id: payload.product_id,
              workload_id: payload.workload_id,
              product_external_id: product.external_id,
            }),
            getProfileAction(),
          ])

          const { data } = resultAction
          if (!(resultAction.serverError || data?.error) && data?.data) {
            const eventData = {
              event: 'vitrine',
              eventName: 'subscription',
              customDataObject: {
                environment: 'Vitrine',
                value: data?.data.product.amount,
                subscription_id: data?.data.id,
                external_id:
                  profileResult?.data?.external_id ??
                  profileResult?.data?.reference,
              },
              dedupeId: data?.data.id,
            }

            // const eventData = {
            //   event: 'vitrine',
            //   eventName: 'qualify_lead',
            //   customDataObject: {
            //     environment: 'Vitrine',
            //     item_referrer: referrer,
            //     item_device: device,
            //     ...(status === 'authenticated' && {
            //       user_id: profile?.reference ?? profile?.external_id,
            //       item_name: profile?.full_name,
            //       item_email: profile?.email,
            //     }),
            //   },
            // }

            // trackEvent(eventData)

            const brevoEventPromise = sendEventBrevo({
              event: 'course_inscription',
              properties: {
                course_name: product.name,
                course_id: product.reference,
                ...getBrevoProductProperties(product.product_type),
              },
              eventdata: {
                data: {
                  course_name: product.name,
                  course_id: product.reference,
                  ...getBrevoProductProperties(product.product_type),
                  matricula_id: data?.data.id,
                },
              },
            })

            // Analytics não deve impedir o acesso à sala.
            await Promise.all([brevoEventPromise, trackEvent(eventData)]).catch(
              () => null
            )

            toast.success('Inscrição realizada com sucesso!', {
              description: 'Aguarde o redirecionamento',
            })
            // Navegação completa para garantir que a sala reconheça a nova
            // matrícula (ver comentário equivalente no SubscriptionButton).
            window.location.assign(
              `/app/sala/curso/${product.external_id ?? product.reference}`
            )
          } else {
            toast.error('Erro ao realizar inscrição', {
              description: 'Tente novamente em alguns instantes',
            })
          }
        } catch {
          toast.error('Erro ao realizar inscrição', {
            description: 'Tente novamente em alguns instantes',
          })
        } finally {
          isCreatingEnrolmentRef.current = false
          setSelectedWorkload(null)
          setShouldShowDialogWorkloads(false) // Fecha o dialog após finalizar
        }
      })
    },
    [authStatus, product, router]
  )

  return (
    <>
      <AlertDialog
        onOpenChange={setShouldShowDialogWorkloads}
        open={shouldShowDialogWorkloads}
      >
        {variant === 'primary' ? (
          <Button
            aria-describedby={isPending ? 'loading-enrollment' : undefined}
            className="group relative mt-3 min-h-12 bg-gradient-to-r from-emerald-600 to-emerald-700 px-8 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:from-emerald-700 hover:to-emerald-800 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={isPending}
            onClick={handleCheckExistEnrolment}
          >
            {isPending ? (
              <>
                <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                <span className="sr-only" id="loading-enrollment">
                  Carregando inscrição
                </span>
                Carregando...
              </>
            ) : (
              <>
                <Zap className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                {isSubscriptionFree ? 'INSCREVA-SE GRÁTIS' : 'INSCREVA-SE'}
              </>
            )}
          </Button>
        ) : (
          <button
            className="group relative font-medium text-primary-700 transition-all duration-200 hover:text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isPending}
            onClick={handleCheckExistEnrolment}
            type="button"
          >
            <span className="relative">
              {isPending ? 'Carregando...' : 'Clique aqui.'}
              <span className="-bottom-1 absolute left-0 h-0.5 w-0 bg-primary-600 transition-all duration-300 group-hover:w-full" />
            </span>
          </button>
        )}

        <AlertDialogContent className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] rounded-3xl border-0 bg-white p-0 shadow-2xl data-[state=closed]:animate-out data-[state=open]:animate-in sm:max-w-2xl">
          {/* Header with gradient background */}
          <AlertDialogHeader className="relative overflow-hidden rounded-t-lg bg-gradient-to-br from-primary-600 via-primary-600 to-primary-700 px-8 py-6 text-white">
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 shadow-lg backdrop-blur-sm">
                  <GraduationCap className="h-7 w-7 text-white" />
                </div>
                <div>
                  <AlertDialogTitle className="font-bold text-xl md:text-2xl">
                    Inscrição no Curso
                  </AlertDialogTitle>
                  <p className="mt-1 font-medium text-primary-100 text-sm opacity-90">
                    {product.name}
                  </p>
                </div>
              </div>
              <AlertDialogCancel
                aria-label="Fechar modal de inscrição"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white transition-all duration-200 hover:scale-110 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 sm:h-12 sm:w-12"
                onClick={() => setShouldShowDialogWorkloads(false)}
              >
                <X className="h-7 w-7" />
              </AlertDialogCancel>
            </div>
          </AlertDialogHeader>

          {/* Content */}
          <div className="px-8 pt-6 pb-8">
            {/* Section header */}
            <div className="mb-8 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-primary-50 to-primary-50 shadow-inner">
                <Clock className="h-10 w-10 text-primary-600" />
              </div>
              <AlertDialogDescription className="mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text font-bold text-3xl text-transparent md:text-4xl">
                Escolha a Carga Horária
              </AlertDialogDescription>
              <p className="mx-auto max-w-md text-gray-600 text-lg leading-relaxed">
                Selecione a carga horária que melhor se adapta ao seu ritmo de
                estudos
              </p>
            </div>

            {/* Workload selection grid */}
            <div
              aria-labelledby="workload-options"
              aria-required="true"
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
              role="radiogroup"
            >
              <div className="sr-only" id="workload-options">
                Opções de carga horária disponíveis. Use as setas para navegar e
                Enter para selecionar.
              </div>
              {activeWorkloads.map((workload, index) => {
                const isSelected = selectedWorkload === workload.reference
                const isProcessing = isPending && isSelected
                return (
                  <WorkloadButton
                    index={index}
                    isPending={isPending}
                    isProcessing={isProcessing}
                    isSelected={isSelected}
                    key={workload.reference}
                    onCreateEnrolment={handleCreateEnrolment}
                    workload={workload}
                  />
                )
              })}
            </div>

            {/* Loading state */}
            {isPending && (
              <div
                aria-live="assertive"
                className="mt-8 text-center"
                role="status"
              >
                <div className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-primary-50 to-primary-50 px-6 py-3 shadow-inner">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-300 border-t-primary-600" />
                  <span className="font-semibold text-base text-primary-700">
                    Processando sua inscrição...
                  </span>
                </div>
              </div>
            )}

            {/* Footer info */}
            <div className="mt-8 rounded-2xl bg-gradient-to-r from-gray-50 to-primary-50/50 p-6">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-primary-500" />
                  <span className="font-medium text-gray-700">
                    {product.name}
                  </span>
                </div>
                <div className="rounded-full bg-primary-100 px-3 py-1">
                  <span className="font-semibold text-primary-700 text-xs">
                    {activeWorkloads.length === 1
                      ? '1 opção disponível'
                      : `${activeWorkloads.length} opções disponíveis`}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {shouldShowFormAuth && enrolmentData && (
        <ModalLayout
          enrolmentData={enrolmentData}
          externalIdProduct={product.external_id}
          notLoggedIn={shouldShowFormAuth}
          productType={product.product_type}
          referenceProduct={product.reference}
          setNotLoggedIn={() => {
            setShouldShowFormAuth(false)
            setEnrolmentData(null)
            setSelectedWorkload(null)
          }}
          setShouldShowFormAuth={setShouldShowFormAuth}
        />
      )}
    </>
  )
}
